import {
  Actor,
  ActorEvents,
  Collider,
  CollisionContact,
  CollisionType,
  Color,
  Debug,
  Engine,
  EventEmitter,
  GameEvent,
  Keys,
  PolygonCollider,
  Side,
  vec,
} from "excalibur";
import { Resources } from "./resources";
import { Plant } from "./plant";
import { Constants } from "./const";
import { JunkFood } from "./junk";

// Actors are the main unit of composition you'll likely use, anything that you want to draw and move around the screen
// is likely built with an actor

// They contain a bunch of useful components that you might use
// actor.transform
// actor.motion
// actor.graphics
// actor.body
// actor.collider
// actor.actions
// actor.pointer

type HerbyEvents = {
  herbydied: HerbyDiedEvent;
  herbyate: HerbyAteEvent;
};

export class HerbyDiedEvent extends GameEvent<Herby> {
  constructor(
    public target: Herby,
    public killer: JunkFood,
  ) {
    super();
  }
}

export class HerbyAteEvent extends GameEvent<Herby> {
  constructor(
    public target: Herby,
    public food: Plant,
  ) {
    super();
  }
}

export class Herby extends Actor {
  public override events = new EventEmitter<ActorEvents & HerbyEvents>();
  private isGrounded = false;

  constructor() {
    super({
      // Giving your actor a name is optional, but helps in debugging using the dev tools or debug mode
      // https://github.com/excaliburjs/excalibur-extension/
      // Chrome: https://chromewebstore.google.com/detail/excalibur-dev-tools/dinddaeielhddflijbbcmpefamfffekc
      // Firefox: https://addons.mozilla.org/en-US/firefox/addon/excalibur-dev-tools/
      name: "Herby",
      pos: vec(100, 0),
      width: 100,
      height: 100,
      // anchor: vec(0, 0), // Actors default center colliders and graphics with anchor (0.5, 0.5)
      collisionType: CollisionType.Active, // Collision Type Active means this participates in collisions and physics resolution
    });
    this.collider.set(
      new PolygonCollider({
        points: [
          vec(52, 7),
          vec(59, 1),
          vec(79, 1),
          vec(100, 20),
          vec(100, 30),
          vec(93, 42),
          vec(80, 42),
          vec(80, 70),
          vec(85, 76),
          vec(85, 90),
          vec(70, 99),
          vec(20, 99),
          vec(0, 85),
          vec(0, 76),
          vec(34, 42),
          vec(52, 41),
        ].map((v) => v.sub(vec(50, 50))), // center the collider points
      }),
    );

    // Configure physics body with manual gravity (more control)
    this.body.useGravity = false; // We'll handle gravity ourselves
    this.body.acc = vec(0, Constants.Gravity); // Set acceleration (gravity)
  }

  override onInitialize() {
    // Generally recommended to stick logic in the "On initialize"
    // This runs before the first update
    // Useful when
    // 1. You need things to be loaded like Images for graphics
    // 2. You need excalibur to be initialized & started
    // 3. Deferring logic to run time instead of constructor time
    // 4. Lazy instantiation
    const sprite = Resources.Herby.toSprite();
    sprite.height = 100;
    sprite.width = 100;
    this.graphics.add(sprite);
  }

  protected jumping = false;

  private isInputActive(engine: ex.Engine) {
    // if the space bar or the first pointer was down
    return (
      engine.input.keyboard.isHeld(Keys.Space) ||
      engine.input.pointers.isDown(0)
    );
  }

  override onPreUpdate(engine: Engine, elapsedMs: number): void {
    // Put any update logic here runs every frame before Actor builtins
  }

  override onPostUpdate(engine: Engine, elapsedMs: number): void {
    const inputActive = this.isInputActive(engine);

    // Jump logic - apply impulse (instant velocity change)
    if (inputActive && !this.jumping) {
      this.body.applyLinearImpulse(
        vec(
          0,
          this.isGrounded ? Constants.JumpImpulse : Constants.JumpImpulse / 4,
        ),
      );
      this.jumping = true;
      this.isGrounded = false; // Leave ground immediately when jumping
    }

    // Reset jumping flag when input released
    if (!inputActive) {
      this.jumping = false;
    }

    // Limit fall speed (terminal velocity)
    if (this.vel.y > Constants.MaxFallSpeed) {
      this.vel.y = Constants.MaxFallSpeed;
    }

    Debug.drawBounds(this.collider.bounds, { color: Color.Yellow });
  }

  override onPreCollisionResolve(
    self: Collider,
    other: Collider,
    side: Side,
    contact: CollisionContact,
  ): void {
    // Called before a collision is resolved, if you want to opt out of this specific collision call contact.cancel()
  }

  override onPostCollisionResolve(
    self: Collider,
    other: Collider,
    side: Side,
    contact: CollisionContact,
  ): void {
    // Called every time a collision is resolved and overlap is solved
  }

  override onCollisionStart(
    self: Collider,
    other: Collider,
    side: Side,
    contact: CollisionContact,
  ): void {
    console.log(`Herby has collided with ${other} on side ${side}`);
    if (other.owner?.name === "Ground" && side === Side.Bottom) {
      this.isGrounded = true;
    }
    if (other.owner instanceof Plant) {
      console.log(`Herby ate a ${other.owner.Type} plant!`);
      other.owner.kill();
      this.events.emit("herbyate", new HerbyAteEvent(this, other.owner));
    }
    if (other.owner instanceof JunkFood) {
      this.events.emit("herbydied", new HerbyDiedEvent(this, other.owner));
    }
    super.onCollisionStart(self, other, side, contact);
  }

  override onCollisionEnd(
    self: Collider,
    other: Collider,
    side: Side,
    lastContact: CollisionContact,
  ): void {
    // Check if leaving ground
    if (other.owner?.name === "Ground" && side === Side.Bottom) {
      this.isGrounded = false;
    }
  }
}
